import { ActionType, ProColumns } from '@ant-design/pro-components';
import { Breadcrumb, Form, Input, InputNumber } from 'antd';
import { ROUTES } from 'base/routes/routes';
import BoxContainer from 'base/ui/components/BoxContainer';
import CustomButton from 'base/ui/components/Button';
import CustomSelect from 'base/ui/components/CustomSelect';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import * as Icon from 'base/ui/components/Icons';
import useDebounce from 'hooks/useDebounce';
import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react';
import { ButtonEdit } from 'pages/Components/FunctionButton';
import PageWrapper from 'pages/Components/PageWrapper';
import { SearchBox, SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { SearchModel } from 'pages/model';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CommonModel } from 'services/Main/common_services';
import { RemindModel } from 'services/Masterdata/warrantyRemind_services';
import { commonPresenter, commonStore, warrantyRemindPresenter, warrantyRemindStore } from 'stores/root_store';
import { PAGE_SIZE_DEFAULT } from 'utils/constants';


export type TableListItem = {
  key: number;
  stt: number;
  nhomSP: string;
  typeSP: string;
  maSP: string;
  sanpham: string;
  typeWarranty: string;
  thoiHanKyTruoc: string;
  thoiHanKyNay: string;
  timeApply: string;
  timeExpire: string;
};

const arrStatus = [
  { key: true, value: 'Đang sử dụng' },
  { key: false, value: 'Ngưng sử dụng' }
]

const defaultColumn: ProColumns<RemindModel>[] = [
  {
    title: 'STT',
    dataIndex: 'stt',
    fixed: true,
    width: 50,
    align: 'center',
  },
  {
    title: 'ID',
    dataIndex: 'remindConfigCode',
    fixed: true,
    width: 80,
  },
  {
    title: 'Cấu hình theo',
    dataIndex: 'remindConfigName',
    width: 120, fixed: true,
  },
  {
    title: 'Công ty',
    dataIndex: 'companyName',
    width: 240, fixed: true,
  },
  {
    title: 'Phân loại vật tư',
    dataIndex: 'phanLoaiVatTuName',
    width: 180,
  },
  {
    title: 'Nhóm vật tư',
    dataIndex: 'nhomVatTuName',
    width: 180,
  },
  {
    title: 'Mã SAP sản phẩm',
    dataIndex: 'sapCode',
    width: 140,
  },
  {
    title: 'Tên sản phẩm',
    dataIndex: 'productName',
    width: 250,
    ellipsis: true
  },
  {
    title: 'Tần suất lặp lại',
    dataIndex: 'frequency',
    width: 120,
    align: 'right',
  },
  {
    title: 'Ngày tạo',
    dataIndex: 'createTime',
    align: 'center',
    width: 180,
    valueType: 'dateTime',
    fieldProps: {
      format: 'DD/MM/YYYY HH:mm:ss',
    },
    onCell: (_, index) => ({
      className: 'text-center',
    }),
  },
  {
    title: 'Người tạo',
    dataIndex: 'createBy',
    width: 180,
  },
  {
    title: 'Trạng thái',
    dataIndex: 'actived',
    align: 'center',
    width: 100,
    render: (_, record) =>
      record.actived ? (
        <Icon.CheckStatus width="14" className="!fill-[#0052cc] block mx-auto" />
      ) : (
        <Icon.CloseStatus width="14" className="!fill-red-500 block mx-auto" />
      ),
  },
  {
    title: 'Chức năng',
    dataIndex: 'chucNang',
    key: 'chucNang',
    width: 100,
    render: (_, record) => (
      <Link to={ROUTES.MASTER_DATA.WARRANTY_REMINDER.DETAIL.LINK + record.remindConfigId}>
        <ButtonEdit />
      </Link>
    ),
  },
];


const createWarrantyReminder = () => {
  return observer(() => {
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm();
    const tableRef = useRef<ActionType>();

    //=====================================CHUYỂN NGÔN NGỮ=====================================//
    const { t } = useTranslation();

    //--- DATA TABLE ------------------------------------------------------------
    const [dataPaging, setDataPaging] = useState<SearchModel>({
      paging: {
        pageSize: PAGE_SIZE_DEFAULT,
        pageIndex: 1,
        orderBy: undefined,
        orderByDesc: undefined,
      },
      actived: true
    });

    const [columns, setColumn] = useState<ProColumns<RemindModel>[]>(defaultColumn);
    // Công ty
    const [listCompany, setListCompany] = useState<CommonModel[]>([]);
    const [listMateGroup, setListMateGroup] = useState<CommonModel[]>([]);
    const [listMateClass, setListMateClass] = useState<CommonModel[]>([]);
    const [listConfig, setListConfig] = useState<CommonModel[]>([]);



    // data table
    const [dataTable, setDataTable] = useState<RemindModel[]>([]);


    const showDrawer = () => {
      setOpen(true);
    };
    const onClose = () => {
      setOpen(false);
    };

    // =================================================================================================================
    // =================================================================================================================
    // =================================================================================================================

    // Công ty
    const getDataCompany = async () => {
      await commonPresenter.getCompanyByAccount(commonStore);
      if (commonStore.commonRespone.isSuccess) {
        setListCompany(commonStore.commonRespone.data);
      }
    };
    // Nhóm vật tự
    const getDataMateGroup = async (key?: string) => {
      await commonPresenter.getListMaterialGroup(commonStore, {
        key: key,
      });
      if (commonStore.commonRespone.isSuccess) {
        setListMateGroup(commonStore.commonRespone.data);
      }
    };
    const debounceSearchMateGroup = useDebounce(getDataMateGroup, 300)

    // Phân loại vật tư
    const getMateClass = async (key?: string) => {
      await commonPresenter.getListMaterialClass(commonStore, {
        key: key
      });
      if (commonStore.commonRespone.isSuccess) {
        setListMateClass(commonStore.commonRespone.data);
      }
    };
    const debounceSearchMateClass = useDebounce(getMateClass, 300)

    // Cấu hình theo
    const getListConfig = async () => {
      await commonPresenter.getCommonDataByKey(commonStore, 'RemindConfigType');
      if (commonStore.commonRespone.isSuccess) {
        setListConfig(commonStore.commonRespone.data);
      }
    };


    const getDataTable = async (dataSearch: any) => {
      await warrantyRemindPresenter.getDataListWarrantyRemind(warrantyRemindStore, dataSearch);
      if (warrantyRemindStore.warrantyRemindRespone.isSuccess) {
        const dataTemp = cloneDeep(warrantyRemindStore.warrantyRemindRespone.data);
        setDataTable(dataTemp);
        return dataTemp;
      }
      setDataTable([]);
      return [];
    };



    useEffect(() => {
      getDataCompany()
      getListConfig()
      getMateClass()
      getDataMateGroup()
    }, [])

    return (
      <PageWrapper
        breadcrumb={[{
          text: 'CẤU HÌNH THỜI GIAN NHẮC NHỞ BẢO HÀNH'
        }]}
        extras={
          <>
            <Link to={ROUTES.MASTER_DATA.WARRANTY_REMINDER.CREATE.LINK}>
              <CustomButton type="primary" icon={<Icon.Plus className="!fill-white" />}>
                {t('btn.create')}
              </CustomButton>
            </Link>
            <CustomButton onClick={showDrawer} type="primary" color="#EFF4F9" className="!px-0 w-8">
              <Icon.Filter className="!fill-primary" />
            </CustomButton>
          </>
        }
      >
        <BoxContainer>
          <CustomProTable<RemindModel>
            columns={columns}
            size="small"
            dataSource={dataTable}
            scroll={{ x: 2300, y: 500 }}
            search={false}
            actionRef={tableRef}
            request={async (params = {}, sort, filter) => {
              const data = await getDataTable({
                ...dataPaging,
                paging: {
                  ...dataPaging.paging,
                  orderBy:
                    Object.values(sort)[0] === 'ascend'
                      ? `${Object.keys(sort)[0].split(',').pop()}`
                      : undefined,
                  orderByDesc:
                    Object.values(sort)[0] === 'descend'
                      ? `${Object.keys(sort)[0].split(',').pop()}`
                      : undefined,
                },
              });

              if (Object.values(sort).length) {
                setDataPaging({
                  ...dataPaging,
                  paging: {
                    ...dataPaging.paging,
                    orderBy:
                      Object.values(sort)[0] === 'ascend'
                        ? `${Object.keys(sort)[0].split(',').pop()}`
                        : undefined,
                    orderByDesc:
                      Object.values(sort)[0] === 'descend'
                        ? `${Object.keys(sort)[0].split(',').pop()}`
                        : undefined,
                  },
                });
              }
              return {
                data: data,
                success: warrantyRemindStore.warrantyRemindRespone.isSuccess,
              };
            }}

            pagination={{
              ...tablePagination(dataPaging, setDataPaging),
              total: warrantyRemindStore.warrantyRemindRespone.paging?.totalCount,
            }}
            rowKey="stt"
          />
        </BoxContainer>

        {/* Form search */}
        <SearchBox
          onClose={onClose}
          className="lg:!w-[50%]"
          open={open}
          footer={
            <CustomButton
              htmlType="submit"
              className="my-auto ml-auto"
              type="primary"
              onClick={async () => {
                form.submit()
                onClose();
              }}
            >
              {t('btn.search')}
            </CustomButton>
          }
        >
          <SearchForm initialValues={{ actived: true }} form={form} column="col-2"
            onFinish={async (value) => {

              setDataPaging({
                paging: {
                  pageSize: PAGE_SIZE_DEFAULT,
                  pageIndex: 1,
                },
                ...Object.fromEntries(Object.entries(value).filter(([_, e]) => {
                  if (typeof e === 'boolean') {
                    return true
                  } else if (Array.isArray(e) && e.length === 0) {
                    return false
                  }
                  else {
                    return !!e
                  }
                }))



              })
              tableRef.current?.reload()
            }
            }
          >
            {/* Công ty */}
            <SearchItem
              name={"companyCode"}
              label={t("search-form.company")}
            >
              <CustomSelect
                allowClear
                options={listCompany}
                fieldNames={{ label: "value", value: "key" }}
                showSearch
                filterOption={(input, option) =>
                  (option?.value ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </SearchItem>

            {/* Phân loại vật tư */}
            <SearchItem
              name={"phanLoaiVatTuList"}
              label={t("search-form.material-class")}
            >
              <CustomSelect
                allowClear
                mode='multiple'
                options={listMateClass}
                fieldNames={{ label: "value", value: "key" }}
                showSearch
                filterOption={false}
                onSearch={(value) => value ? debounceSearchMateClass(value) : debounceSearchMateClass()}
                onClear={() => debounceSearchMateClass()}

              />
            </SearchItem>

            {/* Nhóm vật tư */}
            <SearchItem
              name={"nhomVatTuList"}
              label={t("search-form.material-group")}
            >
              <CustomSelect
                allowClear
                mode='multiple'
                options={listMateGroup}
                fieldNames={{ label: "value", value: "key" }}
                showSearch
                filterOption={false}
                onSearch={(value) => value ? debounceSearchMateGroup(value) : debounceSearchMateGroup()}
                onClear={() => debounceSearchMateGroup()}
              />
            </SearchItem>
            {/* Mã SAP sản phẩm */}
            <SearchItem
              name={"sapCode"}
              label={t("search-form.sap-product-code")}
            >
              <Input />
            </SearchItem>
            {/* Tên sản phẩm */}
            <SearchItem
              name={"productName"}
              label={t("search-form.product-name")}
            >
              <Input />
            </SearchItem>

            {/* Cấu hình theo */}
            <SearchItem
              name={"remindConfigType"}
              label={'Cấu hình theo'}
            >
              <CustomSelect
                allowClear
                options={listConfig}
                fieldNames={{ label: "value", value: "key" }}
                showSearch
                filterOption={(input, option) =>
                  (option?.value ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </SearchItem>

            {/* Tần suất lặp lại */}
            <SearchItem
              name={"frequency"}
              label={t("search-form.frequency")}
            >
              <InputNumber addonAfter="Tháng/lần" />
            </SearchItem>
            {/* Trạng thái */}
            <SearchItem
              name={"actived"}
              label={t("zalo-notification.status")}
            >
              <CustomSelect
                allowClear
                options={arrStatus}
                fieldNames={{ label: "value", value: "key" }}
                placeholder={'-- Tất cả --'}
              />
            </SearchItem>

          </SearchForm>
        </SearchBox>
      </PageWrapper>
    );
  });
}

export default createWarrantyReminder